import React, { useEffect, useRef } from "react";
import { skillsData } from "../data/portfolioData";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion, revealSectionHeader, EASE } from "../utils/motion";

import {
  SiReact,
  SiVuedotjs,
  SiTailwindcss,
  SiFigma,
  SiCanva,
  SiJavascript,
  SiHtml5,
  SiCss3,
  SiRedux,
  SiNodedotjs,
  SiExpress,
  SiPython,
  SiPhp,
  SiGraphql,
  SiSupabase,
  SiMongodb,
  SiMysql,
  SiPostgresql,
  SiRedis,
  SiAmazonwebservices,
  SiGooglecloud,
  SiDocker,
  SiGithub,
  SiPandas,
  SiScikitlearn,
  SiTensorflow,
  SiSpacy,
  SiNumpy,
} from "react-icons/si";

import { FiServer, FiCpu, FiZap, FiCode, FiDatabase, FiCloud } from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

const CATEGORY_ICONS = {
  "Frontend Development": FiCode,
  "Backend Development": FiServer,
  "Databases": FiDatabase,
  "AI & Data Science": FiCpu,
  "Cloud & DevOps": FiCloud,
};

const CATEGORY_ACCENTS = {
  "Frontend Development": "#00D8FF",
  "Backend Development": "#00A8E1",
  "Databases": "#818CF8",
  "AI & Data Science": "#34D399",
  "Cloud & DevOps": "#FBBF24",
};

const SKILL_ICONS = {
  React: SiReact,
  "React Native": SiReact,
  "Vue.js": SiVuedotjs,
  "Tailwind CSS": SiTailwindcss,
  Figma: SiFigma,
  Canva: SiCanva,
  "JavaScript (ES6+)": SiJavascript,
  HTML5: SiHtml5,
  CSS3: SiCss3,
  Redux: SiRedux,
  "Node.js": SiNodedotjs,
  "Express.js": SiExpress,
  Python: SiPython,
  PHP: SiPhp,
  "RESTful APIs": FiServer,
  GraphQL: SiGraphql,
  Supabase: SiSupabase,
  MongoDB: SiMongodb,
  MySQL: SiMysql,
  PostgreSQL: SiPostgresql,
  Redis: SiRedis,
  "Machine Learning": SiTensorflow,
  NLP: FiCpu,
  "Vertex AI": SiGooglecloud,
  Pandas: SiPandas,
  "Scikit-learn": SiScikitlearn,
  "spaCy": SiSpacy,
  NumPy: SiNumpy,
  "AWS (EC2, S3, Lambda)": SiAmazonwebservices,
  "Google Cloud Platform": SiGooglecloud,
  Docker: SiDocker,
  "Git & GitHub": SiGithub,
  "CI/CD": FiZap,
};

const Skills = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const panelsRef = useRef([]);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      // ── Header cascade via shared utility
      if (headerRef.current) {
        revealSectionHeader(headerRef.current, sectionRef.current);
      }

      // ── Panels: perspective reveal — subtle 3D card-flip feel, compositor-safe
      const panels = panelsRef.current.filter(Boolean);
      if (panels.length) {
        gsap.from(panels, {
          opacity: 0,
          y: 44,
          transformPerspective: 700,
          rotateX: 7,
          duration: 0.72,
          ease: EASE.standard,
          stagger: 0.07,
          clearProps: "transform",
          scrollTrigger: {
            trigger: sectionRef.current.querySelector(".skills-panels-grid"),
            start: "top 86%",
            toggleActions: "play none none reset",
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="skills-section" id="skills" ref={sectionRef}>
      <div className="skills-inner">
        <div className="skills-header" ref={headerRef}>
          <span className="section-eyebrow">Capabilities</span>
          <h2 className="section-title">Technical Expertise</h2>
          <p className="section-subtitle">
            A curated stack built for scalable, intelligent, and production-ready applications
          </p>
        </div>

        <div className="skills-panels-grid">
          {skillsData.map((category, index) => {
            const Icon = CATEGORY_ICONS[category.category] || FiZap;
            const accentColor = CATEGORY_ACCENTS[category.category] || "#00A8E1";

            return (
              <div
                key={category.category}
                className="skill-panel"
                ref={(el) => (panelsRef.current[index] = el)}
                style={{ "--panel-accent": accentColor }}
              >
                <div className="skill-panel-accent-bar" aria-hidden="true" />
                <div className="skill-panel-inner">
                  <div className="skill-panel-header">
                    <div className="skill-panel-icon" style={{ color: accentColor }}>
                      <Icon aria-hidden="true" />
                    </div>
                    <div className="skill-panel-meta">
                      <h3 className="skill-panel-title">{category.category}</h3>
                      <span className="skill-panel-count">{category.skills.length} tools</span>
                    </div>
                  </div>

                  <div className="skill-chips">
                    {category.skills.map((skill) => {
                      const SkillIcon = SKILL_ICONS[skill] || FiServer;
                      return (
                        <span key={skill} className="skill-chip" tabIndex={0}>
                          <SkillIcon className="chip-icon" aria-hidden="true" />
                          <span>{skill}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;
